import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { AddIcon, CloseIcon } from "../../../constants/Icons";

const AddJobcard = ({
  onClose,
  onAdd,
  isBtnLoading,
  estimateSuggest,
  setEditModalOpen,
  setEditData,
  setEstimateSuggest,
  titleSuggest,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    titleId: null,
    taskTitle: "", // new field
    amount: "", // new field (optional)
  });

  const [errors, setErrors] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const wrapperRef = useRef(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [highlightedTitleIndex, setHighlightedTitleIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState("");
  const listRef = useRef(null);
  const taskSuggestionRefs = useRef([]);
  // Task title suggestion handling
  const [showTaskTitlePopup, setShowTaskTitlePopup] = useState(false);
  const [taskSuggestions, setTaskSuggestions] = useState(titleSuggest || []);
  const [filteredTaskSuggestions, setFilteredTaskSuggestions] = useState([]);
  const [highlightedTaskIndex, setHighlightedTaskIndex] = useState(-1);
  const taskTitleRef = useRef(null);
  const amountRef = useRef(null);
  const addButtonRef = useRef(null);

  const taskListRef = useRef(null);

  // ---------------- Validation ----------------
  const validateForm = () => {
    const errs = {};

    if (!formData.title?.trim()) {
      errs.title = "Title is required";
    }

    if (!formData.taskTitle?.trim()) {
      errs.taskTitle = "Task title is required";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!validateForm()) return;

    const AddData = {
      taskTitle: formData.taskTitle?.trim(),
      amount: formData?.amount,
    };

    if (formData.titleId) {
      AddData.titleId = formData.titleId;
    }

    if (typeof onAdd === "function") {
      onAdd(AddData);
      return;
    }

    try {
      if (!checkNetwork()) return;
      setBtnLoading(true);

      const data = { titleId: AddData.titleId, taskTitle: AddData.taskTitle };
      if (AddData?.amount) {
        data.amount = CurrencyConverter.toSubunit(AddData.amount, "INR");
      }
      // Step 1: Create the jobcard
      const response = await AxiosInstance.post(`module/jobcard`, data);
      const newJobcard = response.data.results;

      if (
        typeof setEditModalOpen === "function" &&
        typeof setEditData === "function"
      ) {
        setEditData({
          ...newJobcard,
          mediaFile: newJobcard.media?.length ? newJobcard.media[0] : null,
          tasks: newJobcard.task || [],
        });
        setEditModalOpen(true);
      }
      showAlert("success", "Jobcard Added Successfully");
      handleClose();
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      titleId: null,
      taskTitle: "",
      amount: "",
    });
    setErrors({});
    onClose();
  };

  const handleAddNewTitle = async (newTitle) => {
    if (!newTitle || typeof newTitle !== "string") return;

    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    try {
      if (!checkNetwork()) return;
      setBtnLoading(true);

      const response = await AxiosInstance.post(`module/jobcard/type`, {
        title: cleanTitle,
      });

      const newType = response.data?.results;
      if (!newType?._id) throw new Error("Failed to create new title type.");

      const updated = [
        { _id: newType._id, title: cleanTitle },
        ...(estimateSuggest || []).filter(
          (s) => s.title?.toLowerCase() !== cleanTitle.toLowerCase(),
        ),
      ];

      if (typeof setEstimateSuggest === "function") {
        setEstimateSuggest(updated);
      }

      setSuggestions(updated);

      setFormData((prev) => ({
        ...prev,
        title: cleanTitle,
        titleId: newType._id,
      }));

      showAlert("success", "New title added successfully!");
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAddNewTaskTitle = (newTitle) => {
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    setTaskSuggestions((prev) => {
      // Avoid duplicates
      const exists = prev.some(
        (t) => t.title.toLowerCase() === cleanTitle.toLowerCase(),
      );
      if (exists) return prev;

      const updated = [{ title: cleanTitle }, ...prev];
      return updated;
    });

    // Update filtered suggestions too (so it appears immediately in dropdown)
    setFilteredTaskSuggestions((prev) => {
      const exists = prev.some(
        (t) => t.title.toLowerCase() === cleanTitle.toLowerCase(),
      );
      if (exists) return prev;
      return [{ title: cleanTitle }, ...prev];
    });

    // Update the form value
    setFormData((prev) => ({
      ...prev,
      taskTitle: cleanTitle,
    }));

    clearFieldError("taskTitle");
    // Close the popup
    setShowTaskTitlePopup(false);
  };

  useEffect(() => {
    if (formData.title?.trim()) {
      clearFieldError("title");
    }
  }, [formData.title]);

  useEffect(() => {
    if (formData.taskTitle?.trim()) {
      clearFieldError("taskTitle");
    }
  }, [formData.taskTitle]);

  useEffect(() => {
    if (titleSuggest && titleSuggest.length) {
      setTaskSuggestions(titleSuggest);
    }
  }, [titleSuggest]);

  useEffect(() => {
    if (showSuggestions) {
      setFilteredSuggestions(suggestions);
    }
  }, [suggestions, showSuggestions]);

  useEffect(() => {
    if (!listRef.current || highlightedTitleIndex < 0) return;
    const items = listRef.current.children;
    if (!items || !items[highlightedTitleIndex]) return;
    items[highlightedTitleIndex].scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [highlightedTitleIndex]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setHighlightedTitleIndex(-1);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If click is outside the suggestions container, close popup
      if (taskListRef.current && !taskListRef.current.contains(event.target)) {
        setShowTaskTitlePopup(false);
        setHighlightedTaskIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (estimateSuggest) {
      setSuggestions(estimateSuggest);
    }
  }, [estimateSuggest]);
  useEffect(() => {
    if (
      highlightedTaskIndex >= 0 &&
      taskSuggestionRefs.current[highlightedTaskIndex]
    ) {
      taskSuggestionRefs.current[highlightedTaskIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedTaskIndex]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);
  // ---------------- JSX ----------------
  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show d-block"
        id="AddJobcardModal"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Add JobCard</h6>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <div className="mb-4" ref={wrapperRef}>
                <label className="form-label">
                  Title <span className="text-danger">*</span>
                </label>

                <div className="position-relative">
                  <div
                    className={`form-control d-flex justify-content-between align-items-center ${
                      errors.title ? "is-invalid" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setShowSuggestions(!showSuggestions);
                      setSearchTerm("");
                      setHighlightedTitleIndex(-1);
                    }}
                    autoFocus
                  >
                    <span className={formData.title ? "" : "text-muted"}>
                      {formData.title || "Select or add title"}
                    </span>
                    <i
                      className={`bx bx-chevron-${
                        showSuggestions ? "up" : "down"
                      }`}
                    ></i>
                  </div>

                  {showSuggestions && (
                    <div
                      className="dropdown-menu show w-100 p-2 mt-1 rounded shadow-sm"
                      style={{ zIndex: 1000 }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search or add new..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            titleId: null,
                          }));
                          clearFieldError("title");
                          setHighlightedTitleIndex(0);
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();

                            // total count includes the "Add new" button if it's visible
                            const totalItems =
                              filteredSuggestions.filter((item) =>
                                item.title
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase()),
                              ).length || 0;

                            const totalCount = totalItems > 0 ? totalItems : 1; // 1 for "Add" button
                            setHighlightedTitleIndex((prev) =>
                              prev < totalCount - 1 ? prev + 1 : 0,
                            );
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();

                            const totalItems =
                              filteredSuggestions.filter((item) =>
                                item.title
                                  .toLowerCase()
                                  .includes(searchTerm.toLowerCase()),
                              ).length || 0;

                            const totalCount = totalItems > 0 ? totalItems : 1;
                            setHighlightedTitleIndex((prev) =>
                              prev > 0 ? prev - 1 : totalCount - 1,
                            );
                          } else if (e.key === "Enter") {
                            e.preventDefault();

                            const trimmedTerm = searchTerm.trim().toLowerCase();
                            const visibleSuggestions =
                              filteredSuggestions.filter((item) =>
                                item.title.toLowerCase().includes(trimmedTerm),
                              );

                            // ✅ Case 1: Selecting from suggestions
                            if (
                              highlightedTitleIndex >= 0 &&
                              highlightedTitleIndex < visibleSuggestions.length
                            ) {
                              const selected =
                                visibleSuggestions[highlightedTitleIndex];
                              setFormData((prev) => ({
                                ...prev,
                                title: selected.title,
                                titleId: selected._id,
                              }));
                              setShowSuggestions(false);
                              setHighlightedTitleIndex(-1);
                              setTimeout(
                                () => taskTitleRef.current?.focus(),
                                100,
                              );
                              return;
                            }

                            // ✅ Case 2: Highlighted "Add new" button OR no match
                            if (
                              visibleSuggestions.length === 0 ||
                              highlightedTitleIndex ===
                                visibleSuggestions.length
                            ) {
                              if (searchTerm.trim()) {
                                await handleAddNewTitle(searchTerm.trim());
                                setShowSuggestions(false);
                                setHighlightedTitleIndex(-1);
                                setTimeout(
                                  () => taskTitleRef.current?.focus(),
                                  100,
                                );
                              }
                            }
                          }
                        }}
                        autoFocus
                      />

                      {filteredSuggestions.filter((item) =>
                        item.title
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()),
                      ).length > 0 ? (
                        <ul
                          className="suggestions-list"
                          ref={listRef}
                          style={{ maxHeight: "120px" }}
                        >
                          {filteredSuggestions
                            .filter((item) =>
                              item.title
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()),
                            )
                            .map((item, index) => (
                              <li
                                key={item._id || index}
                                className={`suggestion-item ${
                                  index === highlightedTitleIndex
                                    ? "highlighted"
                                    : ""
                                }`}
                                style={{ cursor: "pointer" }}
                                onMouseDown={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    title: item.title,
                                    titleId: item._id,
                                  }));
                                  clearFieldError("title");
                                  setShowSuggestions(false);
                                  setHighlightedTitleIndex(-1);
                                }}
                              >
                                {item.title}
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <div
                          className={`text-primary py-2 px-2 d-flex align-items-center justify-content-center ${
                            highlightedTitleIndex === 0 ? "highlighted" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onMouseDown={async () => {
                            await handleAddNewTitle(searchTerm);
                            setShowSuggestions(false);
                          }}
                        >
                          <AddIcon iClass={"me-2"}/> Add{" "}
                          {searchTerm}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>

              <div className="mb-4 position-relative">
                <label className="form-label">
                  Task Title <span className="text-danger">*</span>
                </label>
                <input
                  ref={taskTitleRef}
                  type="text"
                  className={`form-control ${
                    errors.taskTitle ? "is-invalid" : ""
                  }`}
                  value={formData.taskTitle}
                  placeholder="Enter task title"
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, taskTitle: value }));
                    if (value.trim()) {
                      clearFieldError("taskTitle");
                    }
                    if (value.trim().length > 0) {
                      const filtered = taskSuggestions.filter((item) =>
                        item.title.toLowerCase().includes(value.toLowerCase()),
                      );
                      setFilteredTaskSuggestions(filtered);
                      setShowTaskTitlePopup(true);
                      setHighlightedTaskIndex(-1);
                    } else {
                      setShowTaskTitlePopup(false);
                    }
                  }}
                  onFocus={() => {
                    if (taskSuggestions.length > 0) {
                      setFilteredTaskSuggestions(taskSuggestions);
                      setShowTaskTitlePopup(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (
                        highlightedTaskIndex >= 0 &&
                        filteredTaskSuggestions[highlightedTaskIndex]
                      ) {
                        const selected =
                          filteredTaskSuggestions[highlightedTaskIndex];
                        setFormData((prev) => ({
                          ...prev,
                          taskTitle: selected.title,
                        }));
                        clearFieldError("taskTitle");
                        setTaskSuggestions((prev) => {
                          const updated = [
                            selected,
                            ...prev.filter((t) => t.title !== selected.title),
                          ];
                          return updated;
                        });
                        setShowTaskTitlePopup(false);
                        setHighlightedTaskIndex(-1);
                        // ✅ Move focus to Amount field
                        setTimeout(() => amountRef.current?.focus(), 100);
                      } else if (formData.taskTitle.trim()) {
                        // If it's a new task title
                        handleAddNewTaskTitle(formData.taskTitle.trim());
                        setShowTaskTitlePopup(false);
                        setHighlightedTaskIndex(-1);
                        // ✅ Move focus to Amount field
                        setTimeout(() => amountRef.current?.focus(), 100);
                      }
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      if (filteredTaskSuggestions.length === 0) return;
                      setHighlightedTaskIndex((prev) =>
                        prev < filteredTaskSuggestions.length - 1
                          ? prev + 1
                          : 0,
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      if (filteredTaskSuggestions.length === 0) return;
                      setHighlightedTaskIndex((prev) =>
                        prev > 0
                          ? prev - 1
                          : filteredTaskSuggestions.length - 1,
                      );
                    }
                  }}
                />

                {showTaskTitlePopup && filteredTaskSuggestions.length > 0 && (
                  <ul
                    className="suggestions-list"
                    ref={taskListRef}
                    style={{
                      maxHeight: "120px",
                      overflowY: "auto",
                    }}
                  >
                    {filteredTaskSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        ref={(el) => (taskSuggestionRefs.current[index] = el)}
                        className={`suggestion-item ${
                          highlightedTaskIndex === index ? "highlighted" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                        onMouseDown={() => {
                          setFormData((prev) => ({
                            ...prev,
                            taskTitle: suggestion.title,
                          }));
                          clearFieldError("taskTitle");
                          setTaskSuggestions((prev) => {
                            const updated = [
                              suggestion,
                              ...prev.filter(
                                (t) => t.title !== suggestion.title,
                              ),
                            ];
                            return updated;
                          });
                          setShowTaskTitlePopup(false);
                        }}
                      >
                        {suggestion.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mb-4">
                <label className="form-label">Estimated Amount</label>{" "}
                <span className="text-muted fst-italic small">(Optional)</span>
                <div className="col-lg-6">
                  <input
                    ref={amountRef}
                    className="form-control"
                    value={formData.amount}
                    placeholder="Enter amount"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn btn-outline-secondary text-left"
                onClick={handleClose}
              >
                <CloseIcon iClass={"fs-15"}/> Cancel
              </button>
              {isBtnLoading || btnLoading ? (
                <button className="btn btn-primary text-right" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  ref={addButtonRef}
                  type="button"
                  className="btn text-right"
                  style={{
                    background: "#ffab1d",
                    color: "#000",
                    border: "none",
                  }}
                  onClick={handleSubmit}
                >
                  <AddIcon/> Add
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AddJobcard.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  isBtnLoading: PropTypes.bool,
  estimateSuggest: PropTypes.array,
  setEditModalOpen: PropTypes.func,
  setEditData: PropTypes.func,
};

AddJobcard.defaultProps = {
  onAdd: undefined,
  setEstimateSuggest: undefined,
};

export default AddJobcard;
